/**
 * Project API Service
 */

import { API_BASE_URL } from '../config/env'
import type {
  Project,
  ProjectFilters,
  ProjectListResponse,
  ProjectDetailResponse,
  SortState,
  PaginationState,
  Job
} from '../types/project'

const PROJECTS_API_BASE_URL = import.meta.env.VITE_PROJECTS_API_BASE_URL || '/api/projects'

// Transform API response to frontend types
const transformProject = (apiProject: any): Project => {
  return {
    id: apiProject.project_id || apiProject.id,
    name: apiProject.description || apiProject.name || 'Unnamed Project',
    customer_name: apiProject.customer_name || '-',
    status: mapApiStatus(apiProject.status),
    start_date: apiProject.project_start_date || apiProject.start_date || new Date().toISOString(),
    end_date: apiProject.project_end_date || apiProject.end_date,
    due_date: apiProject.due_date || null,
    sales_order_created_date: apiProject.sales_order_created_date || null,
    days_to_delivery: apiProject.days_to_delivery || null,
    is_overdue: apiProject.is_overdue || false,
    quoted_amount: apiProject.quoted_amount || 0,
    actual_cost: apiProject.total_actual_cost || apiProject.actual_cost || 0,
    profit_margin: apiProject.gross_margin_pct || apiProject.profit_margin || 0,
    completion_percentage: apiProject.overall_completion_pct || apiProject.completion_percentage || 0,
    job_count: apiProject.total_jobs || apiProject.job_count || 0,
    active_job_count: (apiProject.jobs_in_progress || 0) + (apiProject.jobs_ready || 0) + (apiProject.jobs_planning || 0),
    efficiency_score: calculateEfficiencyScore(apiProject),
    budget_variance: apiProject.cost_variance_pct || apiProject.budget_variance || 0,
    budget_variance_amount: apiProject.budget_variance_amount || 0,
    gross_profit: apiProject.gross_profit || 0,
    risk_level: mapRiskLevel(apiProject.project_health || apiProject.risk_level),
    project_manager: apiProject.project_manager_name || apiProject.project_manager || '',
    created_at: apiProject.project_created_date || apiProject.created_at || new Date().toISOString(),
    updated_at: apiProject.updated_at || new Date().toISOString()
  }
}

const transformJob = (apiJob: any): Job => {
  return {
    id: apiJob.job_id || apiJob.id,
    project_id: apiJob.project_id || '',
    job_number: apiJob.job_id || apiJob.job_number || '',
    name: apiJob.part_description || apiJob.name || 'Unnamed Job',
    status: mapJobStatus(apiJob.job_status || apiJob.status),
    quoted_amount: 0, // Not available in API response
    actual_cost: apiJob.total_actual_cost || apiJob.actual_cost || 0,
    profit_margin: 0, // Would need calculation
    start_date: apiJob.scheduled_start_date || apiJob.start_date || new Date().toISOString(),
    end_date: apiJob.scheduled_due_date || apiJob.end_date,
    operation_count: 0, // Not available in API response
    efficiency_score: 0, // Would need calculation
    completion_percentage: apiJob.completion_percentage || 0,
    assigned_team: '', // Not available in API response
    priority: 'medium' // Default priority
  }
}

// Helper functions for mapping API values to frontend types
const mapApiStatus = (apiStatus: string): 'active' | 'completed' | 'on_hold' | 'cancelled' => {
  if (!apiStatus || apiStatus === '') return 'active'
  const status = apiStatus.toLowerCase()
  if (status.includes('complete')) return 'completed'
  if (status.includes('closed')) return 'completed'
  if (status.includes('hold') || status.includes('pause')) return 'on_hold'
  if (status.includes('cancel')) return 'cancelled'
  return 'active'
}

const mapJobStatus = (apiStatus: string): 'not_started' | 'in_progress' | 'completed' | 'cancelled' => {
  if (!apiStatus) return 'not_started'
  const status = apiStatus.toLowerCase()
  if (status === 'complete' || status === 'completed' || status === 'closed') return 'completed'
  if (status === 'ready' || status === 'planning') return 'not_started'
  if (status.includes('progress')) return 'in_progress'
  if (status.includes('cancel')) return 'cancelled'
  return 'not_started'
}

const mapRiskLevel = (apiRisk: string): 'low' | 'medium' | 'high' => {
  if (!apiRisk) return 'low'
  const risk = apiRisk.toLowerCase()
  if (risk.includes('high') || risk.includes('risk')) return 'high'
  if (risk.includes('medium') || risk.includes('warning')) return 'medium'
  return 'low'
}

const calculateEfficiencyScore = (project: any): number => {
  // Simple efficiency calculation based on available data
  const costVariance = project.cost_variance_pct || 0
  const completionPct = project.overall_completion_pct || 0

  if (completionPct === 0) return 0

  // Higher efficiency = lower cost variance and higher completion
  const efficiency = Math.max(0, Math.min(100, 100 - Math.abs(costVariance) + completionPct)) / 2
  return Math.round(efficiency)
}

const mapQuoteStatus = (apiStatus: string): 'draft' | 'sent' | 'accepted' | 'rejected' => {
  if (!apiStatus) return 'draft'
  const status = apiStatus.toLowerCase()
  if (status === 'accepted' || status === 'approved') return 'accepted'
  if (status === 'sent' || status === 'submitted') return 'sent'
  if (status === 'rejected' || status === 'declined') return 'rejected'
  return 'draft'
}

// Helper function to get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  }
}

// Helper to add global date range to params if available
const addGlobalDateRange = (params: URLSearchParams) => {
  // Check if date range context is available (injected by useApiWithDateRange hook)
  const dateRangeContext = (window as any).__CORTEX_DATE_RANGE__
  if (dateRangeContext) {
    // Only add if not already present in params
    if (!params.has('date_from') && dateRangeContext.start_date) {
      params.append('date_from', dateRangeContext.start_date)
    }
    if (!params.has('date_to') && dateRangeContext.end_date) {
      params.append('date_to', dateRangeContext.end_date)
    }
  }
  return params
}

// Handle API responses
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Network error' }))
    throw new Error(error.detail || `HTTP error! status: ${response.status}`)
  }
  return response.json()
}

export const projectService = {
  // New modular endpoints for project detail page
  async getProjectSummary(projectId: string) {
    let params = new URLSearchParams()
    params = addGlobalDateRange(params)
    const url = params.toString() ? `${PROJECTS_API_BASE_URL}/${projectId}/summary?${params}` : `${PROJECTS_API_BASE_URL}/${projectId}/summary`
    const response = await fetch(url, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectAreas(projectId: string) {
    let params = new URLSearchParams()
    params = addGlobalDateRange(params)
    const url = params.toString() ? `${PROJECTS_API_BASE_URL}/${projectId}/areas?${params}` : `${PROJECTS_API_BASE_URL}/${projectId}/areas`
    const response = await fetch(url, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectAreaDetail(projectId: string, areaId: string) {
    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/areas/${areaId}?include_jobs=true&include_materials=true`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectMetrics(projectId: string, areaId?: string) {
    let params = new URLSearchParams()
    if (areaId) params.append('area_id', areaId)
    params = addGlobalDateRange(params)
    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/metrics?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectMaterials(projectId: string, areaId?: string) {
    let params = new URLSearchParams()
    if (areaId) params.append('area_id', areaId)
    params = addGlobalDateRange(params)
    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/materials?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectHoursSummary(projectId: string, areaId?: string) {
    let params = new URLSearchParams()
    if (areaId) params.append('area_id', areaId)
    params = addGlobalDateRange(params)
    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/hours-summary?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectFinancialSummary(projectId: string, areaId?: string) {
    let params = new URLSearchParams()
    if (areaId) params.append('area_id', areaId)
    params = addGlobalDateRange(params)
    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/financials-summary?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectLabour(projectId: string, areaId?: string) {
    let params = new URLSearchParams()
    if (areaId) params.append('area_id', areaId)
    params = addGlobalDateRange(params)
    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/labour?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  async getProjectQuotes(projectId: string, areaId?: string) {
    let params = new URLSearchParams()
    if (areaId) {
      params.append('area_id', areaId)
    }
    params = addGlobalDateRange(params)
    const url = `${PROJECTS_API_BASE_URL}/${projectId}/quotes?${params}`
    const response = await fetch(url, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Get hours analysis for a project

  // Get subcontracts for a project
  async getProjectSubcontracts(projectId: string, areaId?: string) {
    const params = new URLSearchParams()
    if (areaId) {
      params.append('area_id', areaId)
    }
    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/subcontracts?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Get cost breakdown for a project

  // Get work center breakdown for a project

  // Get projects list with filters and pagination
  async getProjects(
    filters: ProjectFilters = {},
    sort: SortState = { field: 'name', direction: 'asc' },
    pagination: { page: number; pageSize: number } = { page: 1, pageSize: 25 }
  ): Promise<ProjectListResponse> {
    let params = new URLSearchParams({
      page: pagination.page.toString(),
      page_size: pagination.pageSize.toString(),
      sort_by: sort.field,
      sort_order: sort.direction,
    })

    // Add filters to params (dates first to prevent duplication)
    if (filters.date_from) {
      params.append('date_from', filters.date_from)
    }
    if (filters.date_to) {
      params.append('date_to', filters.date_to)
    }

    // Add global date range only if not already set by filters
    params = addGlobalDateRange(params)

    // Add other filters
    if (filters.status?.length) {
      params.append('status', filters.status.join(','))
    }
    if (filters.customer) {
      params.append('sales_code_id', filters.customer)
    }
    if (filters.sales_code_id && !filters.customer) {
      params.append('sales_code_id', filters.sales_code_id)
    }
    if (filters.sector) {
      params.append('sector_id', filters.sector)
    }
    if (filters.account_manager_id) {
      params.append('account_manager_id', filters.account_manager_id)
    }
    if (filters.project_manager_id) {
      params.append('project_manager_id', filters.project_manager_id)
    }
    if (filters.min_value !== undefined) {
      params.append('min_value', filters.min_value.toString())
    }
    if (filters.max_value !== undefined) {
      params.append('max_value', filters.max_value.toString())
    }
    if (filters.risk_level?.length) {
      params.append('risk_level', filters.risk_level.join(','))
    }
    if (filters.search) {
      params.append('search', filters.search)
    }
    if (filters.over_budget) {
      params.append('over_budget', 'true')
    }
    if (filters.active_only) {
      params.append('active_only', 'true')
    }

    const response = await fetch(`${API_BASE_URL}/projects/?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Get single project details
  async getProject(id: string): Promise<ProjectDetailResponse> {
    let params = new URLSearchParams()
    params = addGlobalDateRange(params)
    const url = params.toString() ? `${API_BASE_URL}/projects/${id}?${params}` : `${API_BASE_URL}/projects/${id}`
    const response = await fetch(url, {
      headers: getAuthHeaders()
    })
    const apiData = await handleResponse(response)

    // Transform quotes from API to frontend format
    const transformedQuotes = (apiData.quotes || []).map((quote: any, index: number) => ({
      id: quote.quote_id || `quote-${index}`,
      quote_number: quote.quote_id || `Q-${index + 1}`,
      version: String(index + 1), // Use index as version since API doesn't provide it
      quoted_amount: quote.total_quote_cost || 0,
      quoted_hours: quote.total_hours || 0,
      quoted_material: quote.total_material_cost || 0,
      quoted_labour: quote.total_labor_cost || 0,
      created_date: quote.created_date || new Date().toISOString(),
      status: mapQuoteStatus(quote.quote_status)
    }))

    // Transform API response to frontend types
    return {
      project: transformProject(apiData),
      jobs: apiData.jobs?.map(transformJob) || [],
      quotes: transformedQuotes,
      work_center_hours: apiData.work_center_hours || [],
      cost_breakdown: apiData.cost_breakdown || {},
      material_usage: apiData.materials?.map((m: any) => ({
        id: m.part_id,
        material_code: m.part_id,
        material_name: m.part_description || 'Unknown Material',
        supplier: 'Unknown', // Not provided by API
        estimated_qty: m.total_quantity || 0,
        actual_qty: m.total_quantity || 0,
        estimated_cost: m.total_cost || 0,
        actual_cost: m.total_cost || 0,
        variance_qty: 0,
        variance_cost: 0,
        unit: 'EA' // Default unit
      })) || [],
      metrics: {
        revenue_trend: [],
        cost_breakdown: [],
        efficiency_timeline: [],
        margin_trend: [],
        work_center_efficiency: []
      }
    }
  },

  // Get jobs for a project
  async getProjectJobs(
    projectId: string,
    pagination: { page: number; pageSize: number } = { page: 1, pageSize: 25 },
    options: { areaId?: string; status?: string } = {}
  ) {
    let params = new URLSearchParams({
      page: pagination.page.toString(),
      page_size: pagination.pageSize.toString(),
    })

    if (options.areaId) {
      params.append('area_id', options.areaId)
    }
    if (options.status) {
      params.append('status', options.status)
    }

    // Add global date range
    params = addGlobalDateRange(params)

    const response = await fetch(`${PROJECTS_API_BASE_URL}/${projectId}/jobs?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Export projects to CSV
  async exportToCsv(filters: ProjectFilters = {}, filename = 'projects.csv') {
    let params = new URLSearchParams()

    // Pass all filters (matching getProjects)
    if (filters.date_from) params.append('date_from', filters.date_from)
    if (filters.date_to) params.append('date_to', filters.date_to)
    params = addGlobalDateRange(params)
    if (filters.status?.length) params.append('status', filters.status.join(','))
    if (filters.customer) params.append('sales_code_id', filters.customer)
    if (filters.sales_code_id && !filters.customer) params.append('sales_code_id', filters.sales_code_id)
    if (filters.sector) params.append('sector_id', filters.sector)
    if (filters.account_manager_id) params.append('account_manager_id', filters.account_manager_id)
    if (filters.project_manager_id) params.append('project_manager_id', filters.project_manager_id)
    if (filters.min_value !== undefined) params.append('min_value', filters.min_value.toString())
    if (filters.max_value !== undefined) params.append('max_value', filters.max_value.toString())
    if (filters.min_margin !== undefined) params.append('min_margin', filters.min_margin.toString())
    if (filters.max_margin !== undefined) params.append('max_margin', filters.max_margin.toString())
    if (filters.search) params.append('search', filters.search)
    if (filters.over_budget) params.append('over_budget', 'true')
    if (filters.active_only) params.append('active_only', 'true')

    const response = await fetch(`${API_BASE_URL}/projects/export/?${params}`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      return true
    }
    return false
  },

  // Export a single project's data as Excel
  async exportProject(projectId: string, filename = 'project.xlsx') {
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/export?format=excel`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      return true
    }
    return false
  },

  // Get unique sales codes for filter dropdown
  async getCustomers(): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/filters/sales-codes`, {
      headers: getAuthHeaders()
    })
    const data = await handleResponse(response)
    if (data && data.length > 0 && typeof data[0] === 'object') {
      return data.map((item: any) => item.sales_code_name || item.name || item)
    }
    return data
  },

  // Get sectors for filter dropdown
  async getSectors(): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/filters/sectors`, {
      headers: getAuthHeaders()
    })
    const data = await handleResponse(response)
    // Extract sector names from the hierarchy
    if (data && Array.isArray(data)) {
      return data.map((item: any) => item.sector_name || item.sector_id)
    }
    return []
  },

  async getProjectManagers(): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/projects/project-managers/list`, {
      headers: getAuthHeaders()
    })
    const data = await handleResponse(response)
    return data || []
  },

  // Search projects with advanced operators
  async searchProjects(query: string): Promise<Project[]> {
    const params = new URLSearchParams({ q: query })
    const response = await fetch(`${API_BASE_URL}/projects/search/?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  }
}

export default projectService
